import React, { useContext, useEffect, useState, useRef } from 'react';
import { TApplicationsProps, TAppParentMenuItem, TAppSubMenuItem, TPaginationParamsWithLoading } from '../analytics';
import MenuItem from './MenuItem';
import { AppContext } from 'Context/AppContext';
import { AnalyticsContext } from '../Context/AnalyticsContext';
import { PREVIOS_PERIOD } from '../AnalyticsConstants';
import { MenuDownIcon } from 'assests/GmsIcons';
import SubMenu from './SubMenu';

const SmallLoader = () => (
  <div className="tw-flex tw-items-center tw-justify-center tw-ml-2">
    <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-b-2 tw-border-blue-500"></div>
  </div>
);

interface MenuItemWithSubMenuProps {
  title: string;
  onToggle: () => void;
  subscribedItems: string[];
  someSubMenuItemsChecked: boolean;
  allSubMenuItemsChecked: boolean;
  onCheckboxClick: () => void;
  isLoading?: boolean;
  isRadioMode?: boolean;
}

const MenuItemWithSubMenu: React.FC<MenuItemWithSubMenuProps> = ({
  title,
  onToggle,
  subscribedItems,
  someSubMenuItemsChecked,
  allSubMenuItemsChecked,
  onCheckboxClick,
  isLoading = false,
  isRadioMode = false
}) => {

  const isMenuEnabled = subscribedItems.includes(title);

  return (
    <div className={`tw-form-check tw-pe-3 tw-ps-1 tw-justify-between hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`}>
      <div className={`tw-flex tw-justify-between tw-items-center tw-py-1 tw-px-2 tw-w-full`}>
        <div className={`tw-flex tw-items-center ${!isMenuEnabled || isLoading ? 'tw-pointer-events-none' : 'tw-cursor-pointer'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (isMenuEnabled && !isLoading) {
              onCheckboxClick();
            }
          }}
        >
          <input
            type={isRadioMode ? 'radio' : 'checkbox'}
            className="tw-accent-[#091CAA]"
            checked={allSubMenuItemsChecked}
            ref={ref => {
              if (ref && !isRadioMode) {
                ref.indeterminate = someSubMenuItemsChecked && !allSubMenuItemsChecked;
              }
            }}
            onChange={() => { }} // Using controlled pattern
            disabled={!isMenuEnabled || isLoading}
          />
          <label className={`tw-ms-2 ${!isMenuEnabled ? 'tw-text-gray-400' : 'tw-text-[#091E42]'}`}>
            {title}
          </label>
          {isLoading && <SmallLoader />}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          disabled={!isMenuEnabled || isLoading}
          className="tw-p-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke={isMenuEnabled && !isLoading ? "currentColor" : "#a0a0a0"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    </div>
  );
};

export const ApplicationsMenu = ({ menuData, setMenuData }: TApplicationsProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const [subscribedItems, setSubscribedItems] = useState<string[]>([]);
  const appContext = useContext(AppContext);
  const context = useContext(AnalyticsContext);
  const {
    subMenuItems,
    selectedSubMenuItems,
    setSelectedSubMenuItems,
    pagination,
    setPagination,
    fetchSubMenuItems,
    openSubmenu,
    setOpenSubmenu,
    commendSubMenuItems,
    selectedCommendSubMenuItems,
    setSelectedCommendSubMenuItems,
    fetchCommendSubMenuItems
  } = useContext(AnalyticsContext);

  const [configureLoading, setConfigureLoading] = useState(false);
  const [commendLoading, setCommendLoading] = useState(false);

  const [stagedMenuData, setStagedMenuData] = useState<TAppParentMenuItem[]>(menuData);
  const [stagedSubMenuItems, setStagedSubMenuItems] = useState<TAppSubMenuItem[]>(selectedSubMenuItems);
  const [stagedCommendSubMenuItems, setStagedCommendSubMenuItems] = useState<TAppSubMenuItem[]>(selectedCommendSubMenuItems);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const isRadioMode = context?.selectedCompareMenuItem === PREVIOS_PERIOD;

  useEffect(() => {
    if (isRadioMode) {
      const hasAppSelection = stagedMenuData.some(item =>
        item.checked &&
        item.title !== "Guide" &&
        item.title !== "Commend (ARS)" &&
        subscribedItems.includes(item.title)
      );
      const hasConfigureSelection = stagedSubMenuItems.length > 0;
      const hasCommendSelection = stagedCommendSubMenuItems.length > 0;

      if (hasAppSelection && (hasConfigureSelection || hasCommendSelection)) {
        setStagedSubMenuItems([]);
        setStagedCommendSubMenuItems([]);
      }
      else if (!hasAppSelection && !hasConfigureSelection && !hasCommendSelection && subscribedItems.length > 0) {
        const firstApp = subscribedItems.find(item => item !== "Guide" && item !== "Commend (ARS)");
        if (firstApp) {
          const updatedData = [...stagedMenuData];
          updatedData.forEach(item => {
            item.checked = item.title === firstApp && subscribedItems.includes(item.title);
          });
          setStagedMenuData(updatedData);
        }
      }
    }
  }, [isRadioMode, stagedMenuData, stagedSubMenuItems, stagedCommendSubMenuItems, subscribedItems]);

  useEffect(() => {
    const items = appContext.applications.get(appContext?.selectedParty?.name ?? "") || [];
    setSubscribedItems(items);

    const validMenuData = [...menuData].map(item => ({
      ...item,
      checked: item.title !== "Guide" && item.title !== "Commend (ARS)" && item.checked && items.includes(item.title)
    }));

    if (JSON.stringify(validMenuData) !== JSON.stringify(menuData)) {
      setMenuData(validMenuData);
    }

    if (validMenuData.every(item => !item.checked) &&
      selectedSubMenuItems.length === 0 &&
      selectedCommendSubMenuItems.length === 0 &&
      items.length > 0) {
      const initialSelectionData = [...validMenuData].map(item => ({
        ...item,
        checked: item.title === items[0] && item.title !== "Guide" && item.title !== "Commend (ARS)"
      }));

      setMenuData(initialSelectionData);
    }
  }, [appContext?.selectedParty?.name, appContext.applications, menuData, setMenuData, selectedSubMenuItems, selectedCommendSubMenuItems]);

  useEffect(() => {
    if (showMenu) {
      const originalData = JSON.parse(JSON.stringify(menuData));
      const originalSubItems = JSON.parse(JSON.stringify(selectedSubMenuItems));
      const originalCommendSubItems = JSON.parse(JSON.stringify(selectedCommendSubMenuItems));

      const validStagedData = originalData.map((item: any) => ({
        ...item,
        checked: (item.title !== "Guide" && item.title !== "Commend (ARS)") && item.checked && subscribedItems.includes(item.title)
      }));

      if (isRadioMode) {
        if (selectedSubMenuItems.length > 0) {
          validStagedData.forEach((item: any) => {
            if (item.title === "Guide") {
              item.checked = true;
            }
          });
        } else if (selectedCommendSubMenuItems.length > 0) {
          validStagedData.forEach((item: any) => {
            if (item.title === "Commend (ARS)") {
              item.checked = true;
            }
          });
        }
      }

      setStagedMenuData(validStagedData);
      setStagedSubMenuItems(originalSubItems);
      setStagedCommendSubMenuItems(originalCommendSubItems);
      setHasPendingChanges(false);
    }
  }, [showMenu, menuData, selectedSubMenuItems, selectedCommendSubMenuItems, subscribedItems, isRadioMode]);

  useEffect(() => {
    if (!showMenu) {
      const validMenuData = JSON.parse(JSON.stringify(menuData)).map((item: TAppParentMenuItem) => ({
        ...item,
        checked: (item.title !== "Guide" && item.title !== "Commend (ARS)") && item.checked && subscribedItems.includes(item.title)
      }));

      setStagedMenuData(validMenuData);
      setStagedSubMenuItems(JSON.parse(JSON.stringify(selectedSubMenuItems)));
      setStagedCommendSubMenuItems(JSON.parse(JSON.stringify(selectedCommendSubMenuItems)));
      setHasPendingChanges(false);
    }
  }, [menuData, selectedSubMenuItems, selectedCommendSubMenuItems, subscribedItems, showMenu]);

  useEffect(() => {
    if (isRadioMode) {
      if (stagedSubMenuItems.length > 0) {
        const configureItemIndex = stagedMenuData.findIndex(item => item.title === "Guide");

        if (configureItemIndex !== -1) {
          const updatedData = [...stagedMenuData];
          updatedData[configureItemIndex].checked = true;
          setStagedMenuData(updatedData);
        }
      } else if (stagedCommendSubMenuItems.length > 0) {
        const commendItemIndex = stagedMenuData.findIndex(item => item.title === "Commend (ARS)");

        if (commendItemIndex !== -1) {
          const updatedData = [...stagedMenuData];
          updatedData[commendItemIndex].checked = true;
          setStagedMenuData(updatedData);
        }
      }
    }
  }, [isRadioMode, stagedSubMenuItems, stagedCommendSubMenuItems]);

  const handleClickOutside = React.useCallback((e: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      setShowMenu(false);

      if (openSubmenu) {
        setOpenSubmenu(null);
      }
    }
  }, [openSubmenu, setOpenSubmenu]);

  useEffect(() => {
    if (showMenu) {
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu, handleClickOutside]);

  const toggleMenu = () => {
    if (showMenu) {
      setShowMenu(false);

      if (openSubmenu) {
        setOpenSubmenu(null);
      }
    } else {
      setShowMenu(true);
    }
  };

  const closeSubmenu = () => {
    setOpenSubmenu(null);
  };

  const handleLoadMore = () => {
    if (!pagination.loading && pagination.hasMore) {
      setPagination((prev: TPaginationParamsWithLoading) => ({ ...prev, pageNumber: prev.pageNumber + 1 }));
    }
  };

  const handleSubmenuItemClick = (subItem: TAppSubMenuItem, menuType: 'Guide' | 'commend') => {
    setHasPendingChanges(true);

    if (isRadioMode) {
      if (menuType === 'Guide') {
        setStagedSubMenuItems([{ ...subItem, checked: true }]);
        setStagedCommendSubMenuItems([]);

        const updatedData = stagedMenuData.map(item => ({
          ...item,
          checked: item.title === "Guide"
        }));
        setStagedMenuData(updatedData);
      } else {
        setStagedCommendSubMenuItems([{ ...subItem, checked: true }]);
        setStagedSubMenuItems([]);

        const updatedData = stagedMenuData.map(item => ({
          ...item,
          checked: item.title === "Commend (ARS)"
        }));
        setStagedMenuData(updatedData);
      }
      return;
    }

    if (menuType === 'Guide') {
      setStagedSubMenuItems((prev: TAppSubMenuItem[]) => {
        return prev.some(item => item.value === subItem.value)
          ? prev.filter(item => item.value !== subItem.value)
          : [...prev, { ...subItem, checked: true }];
      });
    } else {
      setStagedCommendSubMenuItems((prev: TAppSubMenuItem[]) => {
        return prev.some(item => item.value === subItem.value)
          ? prev.filter(item => item.value !== subItem.value)
          : [...prev, { ...subItem, checked: true }];
      });
    }
  };

  const handleBulkSelection = (checked: boolean, menuType: 'Guide' | 'commend') => {
    if (isRadioMode) return;

    setHasPendingChanges(true);

    if (menuType === 'Guide') {
      setStagedSubMenuItems(checked ? subMenuItems.map((item: TAppSubMenuItem) => ({ ...item, checked })) : []);
    } else {
      setStagedCommendSubMenuItems(checked ? commendSubMenuItems.map((item: TAppSubMenuItem) => ({ ...item, checked })) : []);
    }
  };

  const handleSubmenuSelectAll = (menuType: 'Guide' | 'commend') => handleBulkSelection(true, menuType);
  const handleSubmenuUnselectAll = (menuType: 'Guide' | 'commend') => handleBulkSelection(false, menuType);

  const handleCheckboxChange = (item: TAppParentMenuItem, index: number) => {
    if (!subscribedItems.includes(item.title)) {
      return;
    }

    if (item.title === "Guide" || item.title === "Commend (ARS)") {
      return;
    }

    setHasPendingChanges(true);
    const updatedData = [...stagedMenuData];

    const isLastSelection = stagedMenuData.filter(i =>
      i.checked &&
      i.title !== "Guide" &&
      i.title !== "Commend (ARS)" &&
      subscribedItems.includes(i.title)
    ).length === 1 &&
      item.checked &&
      stagedSubMenuItems.length === 0 &&
      stagedCommendSubMenuItems.length === 0;

    if (!isLastSelection) {
      updatedData[index].checked = !item.checked;
      setStagedMenuData(updatedData);
    }
  };

  const handleRadioChange = (index: number) => {
    const item = stagedMenuData[index];
    if (!subscribedItems.includes(item.title)) {
      return;
    }

    if (item.title === "Guide" || item.title === "Commend (ARS)") {
      return;
    }

    setHasPendingChanges(true);

    const updatedData = stagedMenuData.map((menu, i) => {
      if (i === index) {
        return { ...menu, checked: true };
      } else {
        return { ...menu, checked: false };
      }
    });
    setStagedMenuData(updatedData);
    setStagedSubMenuItems([]);
    setStagedCommendSubMenuItems([]);
  };

  const handleToggle = (parentTitle: string) => {
    if (!subscribedItems.includes(parentTitle)) {
      return;
    }

    if (parentTitle === "Guide") {
      if (subMenuItems?.length === 0 && pagination.pageNumber === 1) {
        fetchSubMenuItems();
      }
    } else if (parentTitle === "Commend (ARS)") {
      if (commendSubMenuItems?.length === 0) {
        fetchCommendSubMenuItems();
      }
    }

    const parentIndex = stagedMenuData.findIndex((dataItem) => dataItem.title === parentTitle);
    if (parentIndex === -1) return;

    if (openSubmenu !== stagedMenuData[parentIndex].value) {
      setOpenSubmenu(stagedMenuData[parentIndex].value);
    } else {
      setOpenSubmenu(null);
    }
  };

  const handleConfigureCheckboxClick = async () => {
    if (!subscribedItems.includes("Guide")) {
      return;
    }

    setHasPendingChanges(true);

    if (isRadioMode) {
      if (subMenuItems.length === 0 && pagination.pageNumber === 1) {
        try {
          setConfigureLoading(true);
          setPagination((prev: TPaginationParamsWithLoading) => ({
            ...prev,
            loading: true
          }));

          await fetchSubMenuItems();
        } catch (error) {
          console.error("Error loading submenu items:", error);
        } finally {
          setPagination((prev: TPaginationParamsWithLoading) => ({
            ...prev,
            loading: false
          }));
          setConfigureLoading(false);
        }
      }

      const configureItem = stagedMenuData.find((dataItem) => dataItem.title === "Guide");
      if (configureItem && openSubmenu !== configureItem.value) {
        setOpenSubmenu(configureItem.value);
      }

      if (stagedSubMenuItems.length > 0) {
        const updatedData = stagedMenuData.map(item => ({
          ...item,
          checked: item.title === "Guide"
        }));
        setStagedMenuData(updatedData);
        setStagedCommendSubMenuItems([]);
      }

      return;
    }

    // Standard checkbox mode logic
    if (subMenuItems.length > 0 && stagedSubMenuItems.length === subMenuItems.length) {
      setStagedSubMenuItems([]);
      return;
    }

    if (subMenuItems.length === 0 && pagination.pageNumber === 1) {
      try {
        setConfigureLoading(true);
        setPagination((prev: TPaginationParamsWithLoading) => ({
          ...prev,
          loading: true
        }));

        const items = await fetchSubMenuItems();

        const itemsToSelect = items && Array.isArray(items) && items.length > 0
          ? items
          : subMenuItems;

        setStagedSubMenuItems(itemsToSelect.map((item: TAppSubMenuItem) => ({
          ...item,
          checked: true
        })));
      } catch (error) {
        console.error("Error loading submenu items:", error);
      } finally {
        setPagination((prev: TPaginationParamsWithLoading) => ({
          ...prev,
          loading: false
        }));
        setConfigureLoading(false);
      }
    } else {
      setStagedSubMenuItems(subMenuItems.map((item: TAppSubMenuItem) => ({
        ...item,
        checked: true
      })));
    }

    const configureItem = stagedMenuData.find((dataItem) => dataItem.title === "Guide");
    if (configureItem && openSubmenu !== configureItem.value) {
      setOpenSubmenu(configureItem.value);
    }
  };

  const handleCommendCheckboxClick = () => {
    if (!subscribedItems.includes("Commend (ARS)")) {
      return;
    }

    setHasPendingChanges(true);

    if (isRadioMode) {
      if (commendSubMenuItems.length === 0) {
        try {
          setCommendLoading(true);
          const items = fetchCommendSubMenuItems();

          if (items.length > 0) {
            setCommendLoading(false);
          }
        } catch (error) {
          console.error("Error loading Commend submenu items:", error);
        } finally {
          setCommendLoading(false);
        }
      }

      const commendItem = stagedMenuData.find((dataItem) => dataItem.title === "Commend (ARS)");
      if (commendItem && openSubmenu !== commendItem.value) {
        setOpenSubmenu(commendItem.value);
      }

      if (stagedCommendSubMenuItems.length > 0) {
        const updatedData = stagedMenuData.map(item => ({
          ...item,
          checked: item.title === "Commend (ARS)"
        }));
        setStagedMenuData(updatedData);
        setStagedSubMenuItems([]);
      }

      return;
    }

    // Standard checkbox mode logic
    if (commendSubMenuItems.length > 0 && stagedCommendSubMenuItems.length === commendSubMenuItems.length) {
      setStagedCommendSubMenuItems([]);
      return;
    }

    if (commendSubMenuItems.length === 0) {
      try {
        setCommendLoading(true);
        const items = fetchCommendSubMenuItems();

        if (items.length > 0) {
          setStagedCommendSubMenuItems(items.map((item: TAppSubMenuItem) => ({
            ...item,
            checked: true
          })));
        }
      } catch (error) {
        console.error("Error loading Commend submenu items:", error);
      } finally {
        setCommendLoading(false);
      }
    } else {
      setStagedCommendSubMenuItems(commendSubMenuItems.map((item: TAppSubMenuItem) => ({
        ...item,
        checked: true
      })));
    }

    const commendItem = stagedMenuData.find((dataItem) => dataItem.title === "Commend (ARS)");
    if (commendItem && openSubmenu !== commendItem.value) {
      setOpenSubmenu(commendItem.value);
    }
  };

  const handleSelectAll = async () => {
    if (isRadioMode) return;

    setHasPendingChanges(true);

    const selectableItems = stagedMenuData.filter(item =>
      subscribedItems.includes(item.title) &&
      item.title !== "Guide" &&
      item.title !== "Commend (ARS)"
    );

    const shouldUnselect = isEverythingSelected();

    if (shouldUnselect) {
      const firstSelectableItem = selectableItems[0];

      if (firstSelectableItem) {
        const updatedData = stagedMenuData.map(item => ({
          ...item,
          checked: item.title === firstSelectableItem.title && subscribedItems.includes(item.title)
        }));

        setStagedMenuData(updatedData);
      }

      setStagedSubMenuItems([]);
      setStagedCommendSubMenuItems([]);
      return;
    }

    const updatedData = stagedMenuData.map(item => ({
      ...item,
      checked: (item.title !== "Guide" && item.title !== "Commend (ARS)") && subscribedItems.includes(item.title)
    }));

    setStagedMenuData(updatedData);

    // Handle Guide selections
    const isConfigureSubscribed = subscribedItems.includes("Guide");
    if (isConfigureSubscribed) {
      if (subMenuItems.length === 0 && pagination.pageNumber === 1) {
        try {
          setConfigureLoading(true);
          setPagination((prev: TPaginationParamsWithLoading) => ({
            ...prev,
            loading: true
          }));

          const items = await fetchSubMenuItems();

          const itemsToSelect = items && Array.isArray(items) && items.length > 0
            ? items
            : subMenuItems;

          setStagedSubMenuItems(itemsToSelect.map((item: TAppSubMenuItem) => ({
            ...item,
            checked: true
          })));
        } catch (error) {
          console.error("Error loading submenu items:", error);
        } finally {
          setPagination((prev: TPaginationParamsWithLoading) => ({
            ...prev,
            loading: false
          }));
          setConfigureLoading(false);
        }
      } else if (subMenuItems.length > 0) {
        setStagedSubMenuItems(subMenuItems.map((item: TAppSubMenuItem) => ({
          ...item,
          checked: true
        })));
      }
    }

    // Handle Commend selections
    const isCommendSubscribed = subscribedItems.includes("Commend (ARS)");
    if (isCommendSubscribed) {
      if (commendSubMenuItems.length === 0) {
        try {
          setCommendLoading(true);
          const items = fetchCommendSubMenuItems();

          if (items.length > 0) {
            setStagedCommendSubMenuItems(items.map((item: TAppSubMenuItem) => ({
              ...item,
              checked: true
            })));
          }
        } catch (error) {
          console.error("Error loading Commend submenu items:", error);
        } finally {
          setCommendLoading(false);
        }
      } else if (commendSubMenuItems.length > 0) {
        setStagedCommendSubMenuItems(commendSubMenuItems.map((item: TAppSubMenuItem) => ({
          ...item,
          checked: true
        })));
      }
    }
  };

  const handleApply = () => {
    if (hasPendingChanges) {
      const validStagedMenuData = stagedMenuData.map(item => {
        if (item.title === "Guide") {
          if (isRadioMode) {
            return {
              ...item,
              checked: stagedSubMenuItems.length > 0
            };
          }
          return item;
        }
        if (item.title === "Commend (ARS)") {
          if (isRadioMode) {
            return {
              ...item,
              checked: stagedCommendSubMenuItems.length > 0
            };
          }
          return item;
        }
        return {
          ...item,
          checked: item.checked && subscribedItems.includes(item.title)
        };
      });

      const hasAnyTopLevelSelection = validStagedMenuData.some(item =>
        item.checked &&
        item.title !== "Guide" &&
        item.title !== "Commend (ARS)"
      );
      const hasAnySubmenuSelection = stagedSubMenuItems.length > 0 || stagedCommendSubMenuItems.length > 0;

      if (!hasAnyTopLevelSelection && !hasAnySubmenuSelection && subscribedItems.length > 0) {
        const firstNonSpecialItem = subscribedItems.find(item =>
          item !== "Guide" && item !== "Commend (ARS)"
        );
        if (firstNonSpecialItem) {
          validStagedMenuData.forEach(item => {
            if (item.title === firstNonSpecialItem) {
              item.checked = true;
            }
          });
        }
      }

      setMenuData(JSON.parse(JSON.stringify(validStagedMenuData)));
      setSelectedSubMenuItems(JSON.parse(JSON.stringify(stagedSubMenuItems)));
      setSelectedCommendSubMenuItems(JSON.parse(JSON.stringify(stagedCommendSubMenuItems)));
      setHasPendingChanges(false);
    }

    setShowMenu(false);
    closeSubmenu();
  };

  const getSelectedApplicationsLength = (): number => {
    const selectedApplications = stagedMenuData
      .filter(item =>
        item.checked &&
        subscribedItems.includes(item.title) &&
        item.title !== "Guide" &&
        item.title !== "Commend (ARS)"
      )
      .length;

    const hasConfigureSubMenuItems = stagedSubMenuItems?.length > 0;
    const hasCommendSubMenuItems = stagedCommendSubMenuItems?.length > 0;
    const configureCount = hasConfigureSubMenuItems ? 1 : 0;
    const commendCount = hasCommendSubMenuItems ? 1 : 0;

    return selectedApplications + configureCount + commendCount;
  };

  const isEverythingSelected = () => {
    const selectableItems = stagedMenuData.filter(item =>
      subscribedItems.includes(item.title) &&
      item.title !== "Guide" &&
      item.title !== "Commend (ARS)"
    );

    const checkedItems = selectableItems.filter(item => item.checked);

    if (isRadioMode) {
      return false;
    }

    if (checkedItems.length === 1 &&
      stagedSubMenuItems.length === 0 &&
      stagedCommendSubMenuItems.length === 0) {
      return false;
    }

    return checkedItems.length > 1 ||
      stagedSubMenuItems.length > 0 ||
      stagedCommendSubMenuItems.length > 0;
  };

  return (
    <div ref={menuRef} className='tw-relative tw-bg-white tw-z-40'>
      <div onClick={toggleMenu} className={`${showMenu ? 'tw-bg-[#EBECF0]' : ''} hover:tw-bg-[#EBECF0] tw-p-2 tw-ms-3 tw-rounded-[4px] tw-flex flex-row tw-items-center tw-justify-center tw-gap-[6px]`}>
        <button className={`tw-text-sm tw-text-[#172B4D]`}>
          Select Applications
          <span className='tw-ms-2'>
            <div className="tw-w-6 tw-h-6 tw-px-3 tw-py-0.5 tw-bg-zinc-200 tw-rounded-3xl tw-border tw-justify-center tw-items-center tw-gap-1 tw-inline-flex">
              <div className="tw-text-slate-600 tw-text-xs">{getSelectedApplicationsLength()}</div>
            </div>
          </span>
        </button>
        <MenuDownIcon />
      </div>
      {showMenu && (
        <div className="tw-origin-top-right tw-absolute tw-mt-1 tw-w-auto tw-mx-3 tw-rounded-[4px] tw-bg-white tw-ring-1 tw-ring-[#EBECF0]">
          <div className='tw-px-2 tw-py-3 tw-border-b tw-border-[#EBECF0] tw-flex tw-items-center tw-justify-between'>
            {!isRadioMode && (
              <>
                <button
                  onClick={handleSelectAll}
                  className="tw-text-sm tw-text-[#0052CC] hover:tw-text-[#0747A6]"
                >
                  {isEverythingSelected() ? "Unselect all" : "Select all"}
                </button>
                <button
                  onClick={handleApply}
                  className="tw-py-1.5 tw-px-2.5 tw-text-sm tw-font-medium tw-bg-[#0052CC] tw-text-white tw-rounded hover:tw-bg-[#0747A6] tw-transition tw-duration-150">
                  Apply
                </button>
              </>
            )}
            {isRadioMode && (
              <button
                onClick={handleApply}
                className="tw-w-full tw-py-1.5 tw-px-4 tw-text-sm tw-font-medium tw-bg-[#0052CC] tw-text-white tw-rounded hover:tw-bg-[#0747A6] tw-transition tw-duration-150">
                Apply
              </button>
            )}
          </div>

          <div className="tw-py-1 tw-text-left" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
            {stagedMenuData.map((item, index) => {
              const isConfigureItem = item.title === "Guide";
              const isCommendItem = item.title === "Commend (ARS)";

              const isItemSelected = !isConfigureItem && !isCommendItem && item.checked && subscribedItems.includes(item.title);

              if (isConfigureItem) {
                return (
                  <div key={item.title} className="tw-relative">
                    <MenuItemWithSubMenu
                      title={item.title}
                      onToggle={() => handleToggle(item.title)}
                      onCheckboxClick={handleConfigureCheckboxClick}
                      subscribedItems={subscribedItems ?? []}
                      someSubMenuItemsChecked={stagedSubMenuItems.length > 0 && stagedSubMenuItems.length < subMenuItems.length}
                      allSubMenuItemsChecked={isRadioMode
                        ? stagedSubMenuItems.length > 0
                        : subMenuItems.length > 0 && stagedSubMenuItems.length === subMenuItems.length}
                      isLoading={configureLoading}
                      isRadioMode={isRadioMode}
                    />

                    {openSubmenu === item.value && subscribedItems.includes("Guide") && (
                      <SubMenu
                        isOpen={openSubmenu === item.value}
                        items={subMenuItems}
                        pagination={pagination}
                        onItemClick={(subItem) => handleSubmenuItemClick(subItem, 'Guide')}
                        onLoadMore={handleLoadMore}
                        onSelectAll={() => handleSubmenuSelectAll('Guide')}
                        onUnselectAll={() => handleSubmenuUnselectAll('Guide')}
                        allSubMenuItemsChecked={subMenuItems.length > 0 && stagedSubMenuItems.length === subMenuItems.length}
                        compareToMenu={context?.selectedCompareMenuItem}
                        selectedItems={stagedSubMenuItems}
                        isRadioMode={isRadioMode}
                        title='Guide'
                      />
                    )}
                  </div>
                );
              } else if (isCommendItem) {
                return (
                  <div key={item.title} className="tw-relative tw-whitespace-nowrap">
                    <MenuItemWithSubMenu
                      title={item.title}
                      onToggle={() => handleToggle(item.title)}
                      onCheckboxClick={handleCommendCheckboxClick}
                      subscribedItems={subscribedItems ?? []}
                      someSubMenuItemsChecked={stagedCommendSubMenuItems.length > 0 && stagedCommendSubMenuItems.length < commendSubMenuItems.length}
                      allSubMenuItemsChecked={isRadioMode
                        ? stagedCommendSubMenuItems.length > 0
                        : commendSubMenuItems.length > 0 && stagedCommendSubMenuItems.length === commendSubMenuItems.length}
                      isLoading={commendLoading}
                      isRadioMode={isRadioMode}
                    />

                    {openSubmenu === item.value && subscribedItems.includes("Commend (ARS)") && (
                      <SubMenu
                        isOpen={openSubmenu === item.value}
                        items={commendSubMenuItems}
                        pagination={{ pageNumber: 1, pageSize: 10, hasMore: false, loading: false }}
                        onItemClick={(subItem) => handleSubmenuItemClick(subItem, 'commend')}
                        onLoadMore={() => { }}
                        onSelectAll={() => handleSubmenuSelectAll('commend')}
                        onUnselectAll={() => handleSubmenuUnselectAll('commend')}
                        allSubMenuItemsChecked={commendSubMenuItems.length > 0 && stagedCommendSubMenuItems.length === commendSubMenuItems.length}
                        compareToMenu={context?.selectedCompareMenuItem}
                        selectedItems={stagedCommendSubMenuItems}
                        isRadioMode={isRadioMode}
                        title='Commend'
                      />
                    )}
                  </div>
                );
              } else {
                return (
                  <div key={item.title} className="tw-relative">
                    <MenuItem
                      title={item.title}
                      isSelected={isItemSelected}
                      onToggle={() => handleToggle(item.title)}
                      onCheckboxChange={() =>
                        !isRadioMode
                          ? handleCheckboxChange(item, index)
                          : handleRadioChange(index)
                      }
                      isOpen={openSubmenu === item.value}
                      subscribedItems={subscribedItems ?? []}
                      compareToMenu={context?.selectedCompareMenuItem}
                      hasSubmenu={false}
                    />
                  </div>
                );
              }
            })}
          </div>
        </div>
      )}
    </div>
  );
};